import React from 'react';
import { UploadTask } from 'firebase/storage';
import { useFormContext } from 'react-hook-form';
import { Stack, Typography } from '@mui/material';

import { FileItem } from './FileItem.tsx';
import { DEFAULT_MAX_FILES_UPLOAD } from './constants.ts';
import { UploadFilesFormData } from './UploadFileModal.tsx';

type UploadedFilesContentProps = {
  items: {
    id: string;
    file: File;
    uploadTask: UploadTask | undefined;
  }[];
  onRemoveFile: (id: number) => void;
  maxFiles?: number;
  isUploading?: boolean;
};

export const UploadedFilesContent = ({
  items,
  onRemoveFile,
  maxFiles = DEFAULT_MAX_FILES_UPLOAD,
  isUploading = false,
}: Readonly<UploadedFilesContentProps>) => {
  const {
    formState: { errors },
  } = useFormContext<UploadFilesFormData, UploadFilesFormData, UploadFilesFormData>();

  if (items.length === 0) {
    return null;
  }

  const isSingleFile = maxFiles === 1;

  return (
    <Stack gap={1}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" sx={{ display: isSingleFile ? 'none' : 'block' }}>
          Document{maxFiles > 1 ? 's' : ''} to upload
        </Typography>
        {maxFiles > 1 && <Typography variant="caption">You can upload up to {maxFiles} files</Typography>}
      </Stack>

      <Stack
        direction="column"
        gap={0.5}
        sx={{
          borderWidth: 1,
          paddingLeft: isSingleFile ? 0 : { xs: 0, sm: 1 },
        }}
      >
        {items.map((field, index) => (
          <FileItem
            key={field.id}
            item={field}
            onRemove={() => onRemoveFile(index)}
            isUploading={isUploading}
            error={errors.files?.[index]?.file}
          />
        ))}
      </Stack>
    </Stack>
  );
};
